// CAD Library Fill tool: for drawings made only of lines, click inside a closed space to make it a panel (like a paint
// bucket). The view's lines are drawn into a picture, the space around the click is filled up to the lines, and its
// outline is traced back into a shape that sits under the lines. A space that isn't closed (the fill would run out of
// the garment) is refused with a plain message.
import type { CadArt, CadShape } from "../../shared/cadlib.js";

const SIZE = 1400; // the picture's longest side, in pixels
const lightness = (hex: string) => (/^#[0-9a-f]{6}$/i.test(hex) ? (0.299 * parseInt(hex.slice(1, 3), 16) + 0.587 * parseInt(hex.slice(3, 5), 16) + 0.114 * parseInt(hex.slice(5, 7), 16)) / 255 : 1);

export class FillRefused extends Error {}

/** The new panel's path (in the drawing's units) for a click at (x, y), or a FillRefused error saying why not. */
export function fillAt(art: CadArt, x: number, y: number): { d: string; box: [number, number, number, number] } {
  const [bx, by, bw, bh] = art.box;
  const k = SIZE / Math.max(bw, bh);
  const W = Math.ceil(bw * k) + 2, H = Math.ceil(bh * k) + 2;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  // Walls: every line, and dark filled shapes that aren't panels (line work drawn as filled shapes).
  const wall = (s: CadShape) => {
    ctx.setTransform(k, 0, 0, k, (1 - bx * k), (1 - by * k));
    if (s.m) ctx.transform(s.m[0], s.m[1], s.m[2], s.m[3], s.m[4], s.m[5]);
    let p: Path2D;
    try { p = new Path2D(s.d); } catch { return; }
    if (!s.area && s.fill !== "none" && s.fill !== "$" && lightness(s.fill) < 0.25) ctx.fill(p, s.eo ? "evenodd" : "nonzero");
    if (s.stroke !== "none") { const scale = Math.hypot(s.m?.[0] ?? 1, s.m?.[1] ?? 0) || 1; ctx.lineWidth = Math.max(s.sw, 1.6 / (k * scale)); ctx.stroke(p); }
  };
  ctx.fillStyle = "#000"; ctx.strokeStyle = "#000"; ctx.lineJoin = "round"; ctx.lineCap = "round";
  for (const s of art.shapes) wall(s);
  const px = ctx.getImageData(0, 0, W, H).data;
  const blocked = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) blocked[i] = px[i * 4 + 3] > 90 ? 1 : 0;
  const sx = Math.round((x - bx) * k + 1), sy = Math.round((y - by) * k + 1);
  if (sx < 0 || sy < 0 || sx >= W || sy >= H) throw new FillRefused("Click inside the garment.");
  if (blocked[sy * W + sx]) throw new FillRefused("That's a line. Click inside the space you want to fill.");
  // Flood fill (scanlines).
  const inRegion = new Uint8Array(W * H);
  const stack = [sx, sy];
  let count = 0;
  while (stack.length) {
    const yy = stack.pop()!, x0 = stack.pop()!;
    let l = x0;
    while (l > 0 && !blocked[yy * W + l - 1] && !inRegion[yy * W + l - 1]) l--;
    let r = x0;
    while (r < W - 1 && !blocked[yy * W + r + 1] && !inRegion[yy * W + r + 1]) r++;
    if (l === 0 || r === W - 1 || yy === 0 || yy === H - 1) throw new FillRefused("That space isn't closed: the fill would run outside the garment. Click inside a closed space, or close the gap in your drawing first.");
    for (let i = l; i <= r; i++) {
      if (inRegion[yy * W + i]) continue;
      inRegion[yy * W + i] = 1; count++;
      for (const ny of [yy - 1, yy + 1]) { const j = ny * W + i; if (!blocked[j] && !inRegion[j]) stack.push(i, ny); }
    }
  }
  if (count > W * H * 0.9) throw new FillRefused("That space isn't closed: the fill would run outside the garment.");
  if (count < 40) throw new FillRefused("That space is too small to be a panel.");
  // Grow the fill a little so it tucks under the lines (no white gap along the edges).
  const grow = 2;
  let mask = inRegion;
  for (let g = 0; g < grow; g++) {
    const next = mask.slice();
    for (let yy = 1; yy < H - 1; yy++) for (let xx = 1; xx < W - 1; xx++) {
      const i = yy * W + xx;
      if (!mask[i] && (mask[i - 1] || mask[i + 1] || mask[i - W] || mask[i + W])) next[i] = 1;
    }
    mask = next;
  }
  const pts = trace(mask, W, H);
  const simple = simplify(pts, 0.9);
  if (simple.length < 3) throw new FillRefused("That space is too small to be a panel.");
  const to = (p: [number, number]): [number, number] => [Math.round(((p[0] - 1) / k + bx) * 100) / 100, Math.round(((p[1] - 1) / k + by) * 100) / 100];
  const out = simple.map(to);
  const xs = out.map((p) => p[0]), ys = out.map((p) => p[1]);
  return { d: `M${out.map((p) => `${p[0]},${p[1]}`).join(" L")} Z`, box: [Math.min(...xs), Math.min(...ys), Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)] };
}

/** The outer edge of a filled region (Moore neighbour tracing), as pixel corners. */
function trace(mask: Uint8Array, W: number, H: number): Array<[number, number]> {
  let start = -1;
  for (let i = 0; i < W * H; i++) if (mask[i]) { start = i; break; }
  if (start < 0) return [];
  const on = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && mask[y * W + x] === 1;
  const dirs: Array<[number, number]> = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
  const sx = start % W, sy = Math.floor(start / W);
  const out: Array<[number, number]> = [[sx, sy]];
  let cx = sx, cy = sy, back = 4; // we came from the left
  for (let steps = 0; steps < W * H * 2; steps++) {
    let found = false;
    for (let t = 1; t <= 8; t++) {
      const d = (back + t) % 8;
      const nx = cx + dirs[d][0], ny = cy + dirs[d][1];
      if (on(nx, ny)) { cx = nx; cy = ny; back = (d + 4) % 8; found = true; break; }
    }
    if (!found || (cx === sx && cy === sy)) break;
    out.push([cx, cy]);
  }
  return out;
}
/** Fewer points along the same outline (Ramer–Douglas–Peucker). */
function simplify(pts: Array<[number, number]>, eps: number): Array<[number, number]> {
  if (pts.length < 4) return pts;
  const keep = new Uint8Array(pts.length);
  keep[0] = 1; keep[pts.length - 1] = 1;
  const stack: Array<[number, number]> = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const [ax, ay] = pts[a], [bx, by] = pts[b];
    const len = Math.hypot(bx - ax, by - ay) || 1;
    let far = -1, dist = 0;
    for (let i = a + 1; i < b; i++) {
      const dd = Math.abs((by - ay) * pts[i][0] - (bx - ax) * pts[i][1] + bx * ay - by * ax) / len;
      if (dd > dist) { dist = dd; far = i; }
    }
    if (far > 0 && dist > eps) { keep[far] = 1; stack.push([a, far], [far, b]); }
  }
  return pts.filter((_, i) => keep[i]);
}
